import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  Optional,
  ViewChild
} from '@angular/core';
import {
  BaseComponent,
  DadataSuggestion,
  DeliveryAddress,
  DadataService,
  YandexMapLoaderService
} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {NbWindowRef} from '@nebular/theme';

declare const ymaps: any;

@Component({
    selector: 'app-popup-address',
    templateUrl: './popup-address.component.html',
    styleUrls: ['./popup-address.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PopupAddressComponent extends BaseComponent implements AfterViewInit {

  submitted = false;
  refresh = true;
  dadataInputValue = '';

  private address = '';
  private mapInstance: any;
  private placemark: any;

  @ViewChild('mapContainer', {static: false}) mapContainer: ElementRef;

  constructor(
    private readonly ngZone: NgZone,
    private readonly dadataService: DadataService,
    private readonly ymapLoader: YandexMapLoaderService,
    @Optional() protected readonly windowRef: NbWindowRef
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  private initMap(): void {
    this.ymapLoader.load().then(() => {
      this.ngZone.run(() => {
        this.mapInstance = new ymaps.Map(this.mapContainer.nativeElement, {
          center: [55.751574, 37.573856],
          zoom: 10,
          controls: ['zoomControl', 'geolocationControl']
        });

        this.mapInstance.events.add('click', (e: any) => {
          const coords = e.get('coords');
          this.onMapClick(coords[0], coords[1]);
        });

        this.cdr.detectChanges();
      });
    });
  }

  onSelect($event: DadataSuggestion | null): void {
    if ($event !== null && 'unrestricted_value' in $event) {
      this.address = $event.unrestricted_value;

      const lat = $event.data?.geo_lat;
      const lon = $event.data?.geo_lon;
      if (lat && lon && this.mapInstance) {
        const coords = [parseFloat(lat), parseFloat(lon)];
        this.setMapCenter(coords);
        this.setPlacemark(coords);
      }
    } else {
      this.address = '';
    }
    this.cdr.detectChanges();
  }

  private onMapClick(lat: number, lon: number): void {
    const coords = [lat, lon];
    this.setPlacemark(coords);
    this.setMapCenter(coords);

    this.dadataService.geolocate(lat, lon, 1).pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(response => {
      this.ngZone.run(() => {
        if (response.suggestions && response.suggestions.length > 0) {
          const suggestion = response.suggestions[0];
          this.address = suggestion.unrestricted_value || suggestion.value;
          this.dadataInputValue = suggestion.value;
        } else {
          this.reverseGeocodeYandex(lat, lon);
        }
        this.cdr.detectChanges();
      });
    });
  }

  private reverseGeocodeYandex(lat: number, lon: number): void {
    ymaps.geocode([lat, lon]).then((result: any) => {
      this.ngZone.run(() => {
        const firstGeoObject = result.geoObjects.get(0);
        if (firstGeoObject) {
          const foundAddress = firstGeoObject.getAddressLine();
          this.address = foundAddress;
          this.dadataInputValue = foundAddress;
          this.cdr.detectChanges();
        }
      });
    });
  }

  private setMapCenter(coords: number[]): void {
    if (this.mapInstance) {
      this.mapInstance.setCenter(coords, 16, {duration: 300});
    }
  }

  private setPlacemark(coords: number[]): void {
    if (this.placemark) {
      this.placemark.geometry.setCoordinates(coords);
    } else if (this.mapInstance) {
      this.placemark = new ymaps.Placemark(coords, {}, {
        preset: 'islands#redDotIcon',
        draggable: false
      });
      this.mapInstance.geoObjects.add(this.placemark);
    }
  }

  onSubmit() {
    if (this.selected()) {
      this.submitted = true;
      this.sendAddress().pipe(
        tap(data => this.handleResponse(data)),
        takeUntil(this.unsubscribe)
      ).subscribe();
    }
  }

  private handleResponse(data) {
    this.submitted = false;
    this.windowRef.close(data);
    this.successToast('Адрес доставки добавлен');
  }

  private sendAddress(): Observable<DeliveryAddress> {
    const ctx = this.windowRef.config.context as {contractId: number};
    return this.compoApiService.post<DeliveryAddress, DeliveryAddress>('/delivery-address', {header: this.address, contractId: ctx.contractId}).pipe(map(response => response.data));
  }

  selected() {
    return this.address !== null && this.address.length;
  }
}
